import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, matchPath } from 'react-router';
import { UserContextState } from '@shared/types';
import { ResponsiveLayout } from '../../components/layout/ResponsiveLayout';
import { ErrorBoundary } from '../../components/ui/ErrorBoundary';
import { MiniPaymentModal } from '../../components/payments/MiniPaymentModal';
import { useFeatureFlags, type FeatureFlagKey } from '../../context/RemoteConfigContext';
import { useShareReceiver } from '../../hooks/useShareReceiver';
import { useShareTargetReceiver } from '../../hooks/useShareTargetReceiver';
import { useShareCredentialSync } from '../../hooks/useShareCredentialSync';
import { pageRoutes, type PageRoute } from '../../pages';

// Loading skeleton for route transitions
const ViewSkeleton: React.FC = () => (
  <div className="h-full w-full flex flex-col items-center justify-center py-20">
    <div className="w-12 h-12 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
  </div>
);

// Feature-flagged route wrapper
const FeatureFlaggedRoute: React.FC<{ flag: FeatureFlagKey; children: React.ReactNode }> = ({ flag, children }) => {
  const { isFeatureEnabled } = useFeatureFlags();

  if (!isFeatureEnabled(flag)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const mainRoutes = pageRoutes.filter(r => r.layout === 'main');
const fullScreenRoutes = pageRoutes.filter(r => r.layout === 'full-screen');

const renderPage = (route: PageRoute, contextState: UserContextState) => {
  const { Component } = route;
  const page = route.withContextState ? <Component state={contextState} /> : <Component />;
  return route.featureFlag
    ? <FeatureFlaggedRoute flag={route.featureFlag}>{page}</FeatureFlaggedRoute>
    : page;
};

interface ViewRouterProps {
  contextState: UserContextState;
  isPaymentModalOpen: boolean;
  setIsPaymentModalOpen: (open: boolean) => void;
  handlePaymentComplete: () => void;
}

export const ViewRouter: React.FC<ViewRouterProps> = ({
  contextState,
  isPaymentModalOpen,
  setIsPaymentModalOpen,
  handlePaymentComplete
}) => {
  const location = useLocation();

  // Internal deep links (appUrlOpen). External/shared URLs are NOT handled here.
  useShareReceiver();
  // Native share sheet → Cart Companion, on a dedicated channel separate from deep links.
  useShareTargetReceiver();
  // Share the Firebase refresh token to the Keychain so the iOS Share Extension can write directly.
  useShareCredentialSync();

  // Deep links (Universal Links / App Links) are handled in AuthGuard so they work when logged out.

  // Full-screen pages (exercise, welcome) — no layout wrapper.
  // Keyed by pathname so a caught render crash resets when the user navigates away.
  const isFullScreenRoute = fullScreenRoutes.some(r => matchPath(r.path, location.pathname));
  if (isFullScreenRoute) {
    return (
      <ErrorBoundary level="view" key={location.pathname}>
        <Suspense fallback={<ViewSkeleton />}>
          <Routes>
            {fullScreenRoutes.map(r => (
              <Route key={r.path} path={r.path} element={renderPage(r, contextState)} />
            ))}
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <>
      <ResponsiveLayout>
        {/* View-level boundary: a crash in one page keeps the nav/layout alive
            instead of unmounting the whole app to the app-level fallback.
            Keyed by pathname so the error state resets on navigation. */}
        <ErrorBoundary level="view" key={location.pathname}>
        <Suspense fallback={<ViewSkeleton />}>
          <Routes>
            {mainRoutes.map(r => (
              <Route key={r.path} path={r.path} element={renderPage(r, contextState)} />
            ))}
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </ResponsiveLayout>

      {/* Modals - rendered outside the layout to be on top */}
      <MiniPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onPaymentComplete={handlePaymentComplete}
      />
    </>
  );
};
